/**
 * 性能监控插件 - 客户端专用
 * 自动监控每个页面的性能指标
 *
 * 功能：
 * 1. 监控首次页面加载性能（FCP, TTFB 等）
 * 2. 监控客户端路由跳转性能（可选）
 * 3. 自动上报性能异常
 */
export default defineNuxtPlugin(() => {
  if (!import.meta.client) return

  const config = useRuntimeConfig()
  const router = useRouter()
  const route = useRoute()

  // 获取当前环境
  const isDev = config.public.MODE === 'dev'

  // 配置项
  const monitorConfig = {
    // 是否启用性能监控（生产环境建议启用，开发环境可选）
    enabled: true,
    // 是否在控制台显示详细日志（建议仅开发环境启用）
    verbose: isDev,
    // 是否监控路由跳转
    monitorRouteChange: isDev, // 生产环境通常不需要

    // ========== 页面监控白名单配置 ==========
    // 监控模式：'all' | 'whitelist' | 'blacklist'
    monitorMode: 'whitelist' as 'all' | 'whitelist' | 'blacklist',

    // 白名单：只监控这些页面（当 monitorMode 为 'whitelist' 时生效）
    whitelist: [
      // '/', // 首页
      '/exhibition', // 展会列表页、详情页
      '/exhibition-' // 展会列表页（会匹配所有 /fair/detail/* 路径）
      // '/information' // 资讯页
      // 可以添加更多需要监控的页面路径
    ],

    // 黑名单：不监控这些页面（当 monitorMode 为 'blacklist' 时生效）
    blacklist: [
      // '/admin', // 后台管理页面
      // '/test' // 测试页面
      // 可以添加不需要监控的页面路径
    ]
  }

  if (!monitorConfig.enabled) return

  /**
   * 检查当前页面是否需要监控
   * @param path 当前页面路径
   * @returns 是否需要监控
   */
  const shouldMonitor = (path: string): boolean => {
    // 开发环境默认监控所有页面
    if (isDev && monitorConfig.monitorMode === 'all') {
      return true
    }

    // 白名单模式：只监控白名单中的页面
    if (monitorConfig.monitorMode === 'whitelist') {
      return monitorConfig.whitelist.some(allowedPath => {
        console.log('66-66', path, allowedPath, path === allowedPath, path.includes(allowedPath))
        // 精确匹配
        // if (path === allowedPath) return true

        if (path == '/' || path.includes(allowedPath)) return true

        // 前缀匹配（如 /fair/detail 可匹配 /fair/detail/123.html）
        // if (path.startsWith(allowedPath + '/')) return true
        return false
      })
    }

    // 黑名单模式：监控除黑名单外的所有页面
    // if (monitorConfig.monitorMode === 'blacklist') {
    //   return !monitorConfig.blacklist.some(blockedPath => {
    //     if (path === blockedPath) return true
    //     if (path.startsWith(blockedPath + '/')) return true
    //     return false
    //   })
    // }

    // 默认监控所有页面
    return true
  }

  // ========== 1. 监控首次页面加载 ==========
  // 检查当前页面是否需要监控
  const currentPath = route.path
  const needsMonitoring = shouldMonitor(currentPath)

  if (needsMonitoring) {
    // 在页面完全加载后执行
    if (document.readyState === 'complete') {
      // 如果页面已经加载完成，立即执行
      getWhiteScreenTime()
    } else {
      // 否则等待 load 事件
      window.addEventListener(
        'load',
        () => {
          getWhiteScreenTime()
        },
        { once: true }
      )
    }

    if (monitorConfig.verbose) {
      console.log(`✅ 性能监控已启用: ${currentPath}`)
    }
  } else {
    if (monitorConfig.verbose) {
      console.log(`⏭️  跳过性能监控: ${currentPath}`)
    }
  }

  // ========== 2. 监控客户端路由跳转（可选）==========
  if (monitorConfig.monitorRouteChange) {
    let routeChangeStart = 0

    // 路由开始跳转
    router.beforeEach((to, from) => {
      // 记录开始时间（排除首次加载）
      if (from.name) {
        routeChangeStart = performance.now()

        // 标记路由跳转开始
        if (performance.mark) {
          performance.mark(`route-start-${to.path}`)
        }
      }
    })

    // 路由跳转完成
    router.afterEach((to, from) => {
      // 检查目标页面是否需要监控
      const shouldMonitorTarget = shouldMonitor(to.path)

      // 只监控客户端路由跳转（排除首次加载）且目标页面在白名单中
      if (from.name && routeChangeStart && shouldMonitorTarget) {
        // 延迟测量，确保页面已渲染
        nextTick(() => {
          setTimeout(() => {
            const duration = Math.round(performance.now() - routeChangeStart)

            // 标记路由跳转结束
            if (performance.mark && performance.measure) {
              performance.mark(`route-end-${to.path}`)
              try {
                performance.measure(`route-navigation-${to.path}`, `route-start-${to.path}`, `route-end-${to.path}`)
              } catch (e) {
                // 忽略测量错误
              }
            }

            // 输出路由跳转性能
            if (monitorConfig.verbose) {
              console.log(`🚀 路由跳转: ${from.path} → ${to.path} (${duration}ms)`)
            }

            // 路由跳转时间超过 3 秒时报警
            if (duration > 3000 && window.fundebug) {
              window.fundebug.notify('路由跳转耗时过长', `${from.path} → ${to.path} 耗时 ${duration}ms`, {
                metaData: {
                  from: from.path,
                  to: to.path,
                  duration,
                  timestamp: Date.now()
                }
              })
            }

            // 重置计时器
            routeChangeStart = 0
          }, 0)
        })
      } else if (from.name && routeChangeStart && !shouldMonitorTarget) {
        // 目标页面不在监控范围内，重置计时器
        routeChangeStart = 0
        if (monitorConfig.verbose) {
          console.log(`⏭️  跳过路由跳转监控: ${to.path}`)
        }
      }
    })
  }

  // ========== 3. Web Vitals 监控（可选，推荐）==========
  // 可以考虑集成 web-vitals 库来监控 LCP, FID, CLS 等指标
  // import { onCLS, onFID, onLCP } from 'web-vitals'
  //
  // onCLS(console.log)
  // onFID(console.log)
  // onLCP(console.log)

  // ========== 4. 长任务监控（可选）==========
  // 监控阻塞主线程的长任务（超过 50ms 的任务）
  if ('PerformanceObserver' in window && monitorConfig.verbose) {
    try {
      const longTaskObserver = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            console.warn(`⚠️ 检测到长任务: ${Math.round(entry.duration)}ms`, entry)
          }
        }
      })

      longTaskObserver.observe({ entryTypes: ['longtask'] })
    } catch (e) {
      // 某些浏览器可能不支持 longtask
    }
  }

  // 提供全局访问方法（可选）
  return {
    provide: {
      // 提供手动触发性能测量的方法
      measurePerformance: () => {
        return getWhiteScreenTime()
      }
    }
  }
})
